package org.example.project.client.config

import io.ktor.http.URLProtocol
import io.ktor.http.Url
import io.ktor.http.hostWithPort

class ApiConfig(
    baseUrl: String,
    val allowInsecureHttp: Boolean = false
) {
    val canonicalOrigin: String
    val normalizedBaseUrl: String
    val scheme: String
    val host: String
    val port: Int

    companion object {
        private val ALLOWED_DEV_HOSTS = setOf(
            "localhost",
            "127.0.0.1",
            "::1",
            "10.0.2.2" // Android emulator loopback alias
        )

        private val ALLOWED_STATIC_ENDPOINTS = setOf(
            "/auth/register",
            "/auth/login",
            "/me",
            "/auth/logout",
            "/activities",
            "/activities/manage",
            "/events",
            "/favorites"
        )

        private val UUID_REGEX = Regex("^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$")

        fun isAllowedEndpoint(clean: String): Boolean {
            if (ALLOWED_STATIC_ENDPOINTS.contains(clean)) return true
            if (clean.startsWith("/activities/manage/")) {
                val id = clean.removePrefix("/activities/manage/")
                return UUID_REGEX.matches(id)
            }
            if (clean.startsWith("/activities/")) {
                val sub = clean.removePrefix("/activities/")
                if (sub.endsWith("/publish")) {
                    val id = sub.removeSuffix("/publish")
                    return UUID_REGEX.matches(id)
                }
                return UUID_REGEX.matches(sub)
            }
            if (clean.startsWith("/events/")) {
                val id = clean.removePrefix("/events/")
                return UUID_REGEX.matches(id) || (id.isNotEmpty() && id.all { it.isLetterOrDigit() || it == '-' || it == '_' })
            }
            if (clean.startsWith("/favorites/")) {
                val id = clean.removePrefix("/favorites/")
                return UUID_REGEX.matches(id) || (id.isNotEmpty() && id.all { it.isLetterOrDigit() || it == '-' || it == '_' })
            }
            return false
        }
    }

    init {
        val trimmed = baseUrl.trim()
        if (trimmed.isEmpty()) {
            throw IllegalArgumentException("Base URL cannot be blank")
        }

        if (trimmed.any { it.isISOControl() || it == '\\' }) {
            throw IllegalArgumentException("Base URL contains illegal control characters or backslashes")
        }

        if (trimmed.contains('?') || trimmed.contains('#') || trimmed.contains('@')) {
            throw IllegalArgumentException("Base URL must not contain query parameters, fragments, or userinfo")
        }

        val lowerTrimmed = trimmed.lowercase()
        if (lowerTrimmed.contains("%2e") || lowerTrimmed.contains("..")) {
            throw IllegalArgumentException("Base URL must not contain path traversal sequences")
        }

        val parsedUrl = try {
            Url(trimmed)
        } catch (_: Exception) {
            throw IllegalArgumentException("Malformed base URL format")
        }

        if (parsedUrl.user != null || parsedUrl.password != null) {
            throw IllegalArgumentException("Base URL must not contain user credentials")
        }

        if (parsedUrl.parameters.names().isNotEmpty()) {
            throw IllegalArgumentException("Base URL must not contain query parameters")
        }

        if (parsedUrl.fragment.isNotEmpty()) {
            throw IllegalArgumentException("Base URL must not contain fragments")
        }

        val protocol = parsedUrl.protocol
        if (protocol != URLProtocol.HTTP && protocol != URLProtocol.HTTPS) {
            throw IllegalArgumentException("Unsupported protocol scheme. Only HTTP and HTTPS are permitted")
        }

        val cleanPath = parsedUrl.encodedPath
        if (cleanPath.isNotEmpty() && cleanPath != "/") {
            throw IllegalArgumentException("Base URL must be root-origin without subpath segments")
        }

        val hostName = parsedUrl.host.lowercase()
        if (hostName.isBlank()) {
            throw IllegalArgumentException("Base URL host cannot be empty")
        }

        if (protocol == URLProtocol.HTTP) {
            val isDevHost = ALLOWED_DEV_HOSTS.contains(hostName)
            if (!allowInsecureHttp || !isDevHost) {
                throw IllegalArgumentException(
                    "Insecure HTTP is permitted only for local dev hosts (localhost, 127.0.0.1, ::1, 10.0.2.2) when allowInsecureHttp is true"
                )
            }
        }

        scheme = protocol.name.lowercase()
        host = hostName
        port = parsedUrl.port
        val isDefaultPort = (protocol == URLProtocol.HTTPS && parsedUrl.port == 443) ||
                            (protocol == URLProtocol.HTTP && parsedUrl.port == 80)
        val authority = if (isDefaultPort || parsedUrl.port == 0) hostName else "$hostName:${parsedUrl.port}"
        canonicalOrigin = "${protocol.name}://$authority"
        normalizedBaseUrl = canonicalOrigin
    }

    fun resolve(endpointPath: String): String {
        val clean = endpointPath.trim()
        val path = if (clean.contains('?')) clean.substringBefore('?') else clean
        val query = if (clean.contains('?')) clean.substringAfter('?') else null

        if (!isAllowedEndpoint(path)) {
            throw IllegalArgumentException("Endpoint path is not a recognized client API endpoint")
        }
        if (query != null) {
            val isSingleEvent = path.startsWith("/events/") && path.length > "/events/".length
            if (!isSingleEvent || !query.matches(Regex("""^expectedVersion=[1-9]\d*$"""))) {
                throw IllegalArgumentException("Query string is not permitted on this endpoint")
            }
        }
        return "$canonicalOrigin$clean"
    }

    fun isSameOrigin(targetUrl: String): Boolean {
        return try {
            val target = Url(targetUrl)
            val isDefaultPort = (target.protocol == URLProtocol.HTTPS && target.port == 443) ||
                                (target.protocol == URLProtocol.HTTP && target.port == 80)
            val targetHost = target.host.lowercase()
            val authority = if (isDefaultPort || target.port == 0) targetHost else "$targetHost:${target.port}"
            val targetOrigin = "${target.protocol.name}://$authority"
            targetOrigin == this.canonicalOrigin
        } catch (_: Exception) {
            false
        }
    }
}
